import {
  Component,
  ViewChild,
  Output,
  Input,
  EventEmitter,
  OnInit,
  ChangeDetectionStrategy
} from "@angular/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { Subject } from "rxjs";
import { getApp } from "../app";
import { Manufacturer } from "../../../../shared/models/manufacturer";

type ManufacturerMatchingModalStep =
  | "currentMatch"
  | "keepCurrentMatch"
  | "discardCurrentMatch"
  | "selectItem"
  | "loadData";

type ManufacturerMatchingModalAction = "save" | "dismiss";
type ManufacturerMatchingModalButtonTextKey =
  | "abort"
  | "confirm"
  | "deleteData"
  | "no"
  | "saveData"
  | "yes";

@Component({
    selector: "app-manufacturer-matching-modal",
    templateUrl: "./manufacturer-matching-modal.component.html",
    styleUrls: ["./manufacturer-matching-modal.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ManufacturerMatchingModalComponent implements OnInit {
  @ViewChild("content", { static: false }) content: any;
  @Output() ok = new Subject<any>();

  @Input() match: any;
  @Input() manufacturer: Manufacturer | null = null;
  @Input() type: "discard" | "save" = "discard";
  @Input() initialStep: ManufacturerMatchingModalStep | null = null;

  step: ManufacturerMatchingModalStep = "currentMatch";
  private readonly buttonTextKeys: Record<
    ManufacturerMatchingModalStep,
    Record<ManufacturerMatchingModalAction, ManufacturerMatchingModalButtonTextKey>
  > = {
    currentMatch: {
      dismiss: "no",
      save: "yes"
    },
    keepCurrentMatch: {
      dismiss: "deleteData",
      save: "saveData"
    },
    discardCurrentMatch: {
      dismiss: "abort",
      save: "confirm"
    },
    selectItem: {
      dismiss: "no",
      save: "yes"
    },
    loadData: {
      dismiss: "abort",
      save: "confirm"
    }
  };
  app = getApp((app) => {
    this.app = app;
  });
  cleanupFields: Set<any> = new Set();
  constructor(private modalService: NgbModal) {}

  ngOnInit() {
    const trimBoxes = [
      "manufacturer.matchingSection",
      "manufacturer.obsolescenceSection",
      "manufacturer.environmentalCompliance",
      "manufacturer.tehnicalSection",
      "manufacturer.supplyChainSection"
    ];
    const fields: string[] = [];
    trimBoxes.forEach((box) => {
      fields.push(...this.app.getList(box));
    });
    this.cleanupFields = new Set(fields);
    this.resetStep();
  }

  async open() {
    try {
      this.resetStep();
      const result = await this.modalService.open(this.content, {
        windowClass: "mediumModal manufacturerMatchingModal"
      }).result;
      this.ok.next(result);
    } catch (err) {
      // NOP
    }
  }

  goToNext(
    step: ManufacturerMatchingModalStep,
    action: ManufacturerMatchingModalAction
  ) {
    if (action === "dismiss") {
      switch (step) {
        case "currentMatch":
        case "discardCurrentMatch":
          this.modalService.dismissAll();
          this.resetStep();
          break;
        case "keepCurrentMatch":
          this.step = "discardCurrentMatch";
          break;
        case "selectItem":
        case "loadData":
          this.modalService.dismissAll();
          this.resetStep();
          break;
      }
    }

    if (action === "save") {
      switch (step) {
        case "currentMatch":
          this.step = "keepCurrentMatch";
          break;
        case "keepCurrentMatch":
          this.save(this.match);
          break;
        case "discardCurrentMatch":
          this.cleanData();
          break;
        case "selectItem":
          this.step = "loadData";
          break;
        case "loadData":
          this.loadEntireData();
          break;
      }
    }
  }

  async save(manufacturer: Manufacturer) {
    const manufacturerDoc = new Manufacturer();
    for (const key of Object.keys(manufacturer)) {
      manufacturerDoc[key] = manufacturer[key];
      if (key === "obsolescenceStatusOverride") {
        manufacturerDoc["obsolescenceStatusOverride"] = true;
      }
      if (manufacturerDoc.edited) {
        manufacturerDoc.edited = false;
      }
      if (manufacturerDoc.z2DataId) {
        manufacturerDoc.z2DataId = "";
      }
    }
    await this.app.manufacturerMatching.updateManufacturer(
      manufacturerDoc,
      manufacturer
    );
    this.resetStep();
    this.modalService.dismissAll();
    location.reload();
  }

  async cleanData() {
    await this.app.manufacturerMatching.cleanUpData(
      this.match,
      this.cleanupFields
    );
    if (this.match.matches.length === 0) {
      sessionStorage.setItem(
        "redoSearch",
        this.match.manufacturerPartNumberRaw
      );
    }
    this.resetStep();
    this.modalService.dismissAll();
    location.reload();
  }

  async loadEntireData() {
    if (this.manufacturer == null || this.match == null) {
      return;
    }
    await this.app.manufacturerMatching.createMatch(
      this.manufacturer,
      this.match
    );

    this.manufacturer = new Manufacturer();
    this.match = {};
    this.resetStep();
    this.modalService.dismissAll();
    location.reload();
  }

  private resetStep() {
    if (this.type === "save") {
      this.step = "selectItem";
      return;
    }

    this.step = this.initialStep || "currentMatch";
  }

  getTitle() {
    if (this.type === "save") {
      return this.app.text.matchingManufacturer.createMatch;
    }

    return this.app.text.matchingManufacturer.currentMatch;
  }

  getSubtitle() {
    if (this.type === "save") {
      return this.app.text.matchingManufacturer.currentMatch;
    }

    return this.app.text.matchingManufacturer.reviewMatchSubtitle;
  }

  getStepIcon() {
    switch (this.step) {
      case "selectItem":
      case "loadData":
        return "far fa-link";
      case "discardCurrentMatch":
        return "far fa-unlink";
      case "keepCurrentMatch":
        return "fas fa-shield-alt";
      default:
        return "fas fa-sitemap fa-rotate-270";
    }
  }

  getButtonText(action: ManufacturerMatchingModalAction) {
    const textKey = this.buttonTextKeys[this.step][action];
    return this.app.text.matchingManufacturerModal[textKey];
  }
}
